import { Box, Button, CircularProgress, Dialog, Divider, IconButton, Skeleton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatVideoDuration, getFileSizeFromBytes, getTimelineIntervals, Icons, latexDelimiters, secondsToHMS } from '../../../utils/utilities'
import VideoPlayer from '../../common/video-player/VideoPlayer'
import VisuallyHiddenInput from '../../common/VisuallyHiddenInput'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import axios from 'axios'
import { observer } from 'mobx-react'
import { useSettingsStore } from '../../../store/settings.store'
import dayjs from 'dayjs'
import Progress from '../../common/Progress'
import CustomChip from '../../common/CustomChip'
import { useOnlineSessionsStore } from '../../../store/onlinesessions.store'
import { useQuizAndQuestionbankStore } from '../../../store/quiz.store'
import Latex from 'react-latex-next'
import CustomSelectField from '../../common/CustomSelectField'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import Message from '../../../shared/components/common/Message'
import noRecordings from '../../../assets/norecording.svg'
import CustomImage from '../../../shared/components/common/CustomImage'
import CustomSearchBar from '../../common/CustomSearchBar'
import ConfirmDialog from '../../common/ConfirmDialog'

const VIDEO_PLAYER_EDIT_OPTIONS = { canScrubTimelineEvents: true }
const loadingSkeleton = <Box>
    {new Array( 3 ).fill( 0 ).map( ( _, idx ) => {
        return ( <Box key={idx} sx={{ borderRadius: 5, bgcolor: t => t.palette.contentBg, p: 1, mb: 0.5 }}>
            <Skeleton variant="text" sx={{ fontSize: "1.4rem", mb: 1, display: "block" }} />
            <Skeleton variant="text" sx={{ width: "70%", fontSize: "1rem", mb: 1, display: "block" }} />
            <Skeleton variant="text" sx={{ width: "45%", fontSize: "1.2rem", mb: 1, display: "block" }} />
        </Box> )
    } )}
</Box>

const RecordingBlock = ( { recording, onSelect } ) => {

    const { border, palette } = useTheme()

    return (
        <Box
            component="button"
            onClick={() => onSelect( recording )}
            sx={{
                border: border[1],
                ":hover": { borderColor: palette.primary.main, boxShadow: `0px 4px 12px -2px ${palette.primary.light + "33"}` },
                ":focus-visible": { borderColor: palette.primary.main, outline: `2px solid ${palette.primary.main}`, outlineOffset: "2px" },
                cursor: "pointer",
                transition: `border-color 150ms ease, box-shadow 150ms ease`,
                bgcolor: palette.contentBg,
                textAlign: "left",
                overflow: "clip",
                borderRadius: 2,
                display: "flex",
                gap: 1,
            }}>
            <CustomImage loading="lazy" containerSx={{ borderRadius: 0 }} src={recording?.thumbnail_metadata?.location} width={250} />
            <Box sx={{ p: 0.5 }}>
                <Typography className='truncate-at-2' variant="h6" sx={{ textTransform: "capitalize", lineHeight: 1.2, fontSize: "1rem" }} component="p">{recording.title}</Typography>
                <Typography sx={{ my: 1, display: "block", fontSize: "0.75rem" }} color="textSecondary" component="span">{recording.topics_covered?.join( ", " )}</Typography>
                <Box sx={{ mt: 0.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <CustomChip value={`Module ${recording?.module_number}${recording?.module_name ? `: ${recording?.module_name}` : ""}`} />
                    <CustomChip value={`Size: ${getFileSizeFromBytes( recording?.recording_metadata?.size )}`} />
                    <CustomChip value={`Duration: ${formatVideoDuration( recording?.recording_metadata?.duration )}`} />
                </Box>
            </Box>
        </Box>
    )
}

const AddNewRecordingButton = observer( ( { quiz, refresh, buttonSize = "medium", buttonVariant = "contained", buttonColor = "secondary", recentlyUsedVideos = [], setTab, classDetails } ) => {

    const [previousAssignmentDuplicateDetails, setPreviousAssignmentDuplicateDetails] = useState( null )
    const [loadingSelectedRecording, setLoadingSelectedRecording] = useState( false )
    const [recordingsFetchError, setRecordingsFetchError] = useState( null )
    const [assignStep, setAssignStep] = useState( "recording-selection" )
    const [availableRecordings, setAvailableRecordings] = useState( [] )
    const [previousAssignments, setPreviousAssignments] = useState( {} )
    const [loadingRecordings, setLoadingRecordings] = useState( false )
    const [selectedRecording, setSelectedRecording] = useState( null )
    const [quizFetchError, setQuizFetchError] = useState( null )
    const [questionEvents, setQuestionEvents] = useState( [] )
    const [isVideoReady, setIsVideoReady] = useState( false )
    const [subjectModule, setSubjectModule] = useState( "" )
    const [titleSearch, setTitleSearch] = useState( "" )
    const [assignMode, setAssignMode] = useState( false )
    const [uploading, setUploading] = useState( false )

    const { palette, layout, border, breakpoints } = useTheme()
    const { meeting_id } = useParams()

    const xs = useMediaQuery( breakpoints.down( 'sm' ) )
    const foundPreviousAssignmentDuplication = !!previousAssignmentDuplicateDetails

    const questionEventsRef = useRef( [] )

    const OnlineSessionsStore = useOnlineSessionsStore()
    const QuizStore = useQuizAndQuestionbankStore()
    const SettingsStore = useSettingsStore()

    const { details: quizDetails } = QuizStore.getQuiz

    const moduleOptions = useMemo( () => Array.from( { length: ( SettingsStore?.getUseSettings['max_modules_per_subject'] || 5 ) }, ( _, i ) => ( { label: `Module ${i + 1}`, value: i + 1 } ) ), [SettingsStore] )

    const cancelUpload = () => {
        setAssignMode( false )
        setSubjectModule( "" )
        setTitleSearch( "" )
        setAssignStep( "recording-selection" )
        setSelectedRecording( null )
    }

    const visibleRecordings = useMemo( () => {
        const query = titleSearch.trim().toLowerCase()
        if ( !query && !subjectModule )
            return availableRecordings
        return availableRecordings.filter( recording => recording.title?.toLowerCase().includes( query ) && ( subjectModule ? recording.module_number === subjectModule : true ) )
    }, [availableRecordings, subjectModule, titleSearch] )

    const handleEventsUpdate = useCallback( events => {
        const formatted = []
        for ( const [idx, event] of Object.entries( events ) ) {
            const eventDetails = {
                label: event.label || event.title,
                id: event.id || idx + 1,
                eventIndex: event.eventIndex || idx,
                data: event.data || {},
                time: event.time,
            }
            formatted.push( eventDetails )
        }
        setQuestionEvents( formatted )
    }, [] )

    const handleRecordingSelect = async ( recording, skipPreviousAssignmentCheck = false ) => {

        setLoadingSelectedRecording( true )
        try {

            if ( !skipPreviousAssignmentCheck && previousAssignments[recording.id] ) {
                const classGroupId = classDetails.section_assignment_id ? `${classDetails.section_assignment_id}-${classDetails.section_short_name}` : String( classDetails.batch_assignment_id )
                const previousAssignmentExists = previousAssignments[recording.id].find( assignment => assignment.assigned_group === classGroupId )
                if ( previousAssignmentExists ) {
                    previousAssignmentExists.recording = recording
                    return setPreviousAssignmentDuplicateDetails( previousAssignmentExists )
                }
            }
            setPreviousAssignmentDuplicateDetails( null )
            setAssignStep( "quiz-config" )
            assignInitialTimelineEvents( recording )
            const { data: { data: result } } = await api.onlineMeetings.fetchHybridClassRecordingDetails( recording.id )
            setSelectedRecording( result )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while fetching recording!" )
        } finally {
            setLoadingSelectedRecording( false )
        }

    }

    const assignInitialTimelineEvents = async recording => {
        try {

            let events = []
            const questions = QuizStore?.getQuestionBank?.questions || []
            const eventIntervals = getTimelineIntervals( recording.recording_metadata.duration, +quizDetails.number_of_question, { includeExtreams: false } ).map( t => Math.ceil( t ) )

            if ( +quizDetails?.number_of_question === +quizDetails?.quiz_questions?.length || questions?.length === +quizDetails?.number_of_question ) {

                let eventQuestions = []
                if ( +quizDetails.number_of_question === +quizDetails.quiz_questions?.length ) {
                    eventQuestions = questions.filter( q => quizDetails.quiz_questions.includes( q.id ) )
                } else {
                    eventQuestions = questions
                }

                events = eventIntervals.map( ( t, idx ) => ( {
                    id: idx + 1,
                    eventIndex: idx,
                    time: t,
                    displayLabel: <Latex delimiters={latexDelimiters}>{questions[idx].question_text}</Latex>,
                    label: `Question ${idx + 1}`,
                    data: {
                        question: questions[idx].id,
                        question_text: questions[idx].question_text

                    }
                } ) )

            } else {
                events = eventIntervals.map( ( t, idx ) => ( {
                    id: idx + 1,
                    eventIndex: idx,
                    time: t,
                    label: `Question ${idx + 1}`,
                    data: {}
                } ) )
            }


            questionEventsRef.current = events
            handleEventsUpdate( [...events] )
        } catch ( err ) {
            console.log( err )
            toast( "Failed to verify the selected the video file! Plese try again." )
        }
    }

    const cancelFromDuplicateAssignment = () => {
        setPreviousAssignmentDuplicateDetails( null )
        setAssignStep( "recording-selection" )
        setSelectedRecording( null )
        setLoadingSelectedRecording( false )
    }

    const assignRecording = async () => {
        setUploading( true )
        try {
            const payload = {
                recording_id: selectedRecording.id,
                timeline_events: questionEvents
            }
            await api.onlineMeetings.assignHybridClassVideoToClass( meeting_id, payload )
            OnlineSessionsStore.fetchOnlineSessionRecordings( meeting_id )
            cancelUpload()
            toast( "Recording has been successfully assigned to the class!" )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to assign the recording to the class! Plese try again." )
        }
    }

    const fetchQuizDetails = useCallback( async () => {
        if ( quiz && quiz.quiz_id ) {

            const { success: qSuccess, message: qErrMsg } = await QuizStore.fetchQuiz( quiz.quiz_id )
            const { question_bank } = QuizStore.getQuiz.details
            const { success: qbSuccess, message: qbErrMsg } = await QuizStore.fetchQuestionBank( question_bank )

            if ( !qSuccess || !qbSuccess ) {
                setQuizFetchError( { message: qErrMsg || qbErrMsg } )
            } else {
                setQuizFetchError( null )
            }

        }
    }, [QuizStore, quiz] )

    const fetchAvailableHybridClassRecordings = useCallback( async () => {
        if ( !classDetails || !assignMode )
            return
        try {
            setLoadingRecordings( true )
            const { data: { data } } = await api.onlineMeetings.fetchHybridClassVideos( { subject_code: classDetails.course_code } )
            setAvailableRecordings( data )
            setRecordingsFetchError( null )
        } catch ( err ) {
            console.log( err )
            setRecordingsFetchError( { message: err?.response?.data?.message || "Something went wrong! Failed to fetch the available recordings.", code: err?.response?.status || 500 } )
        } finally {
            setLoadingRecordings( false )
        }
    }, [classDetails, assignMode] )

    const fetchPreviousAssignments = useCallback( async () => {
        try {
            const { data: { data } } = await api.onlineMeetings.fetchHybridClassVideoAssignments( { subject_code: classDetails.course_code, module_number: subjectModule } )

            const assignments = {}
            for ( const assignment of data ) {
                if ( !assignments[assignment.recording_id] )
                    assignments[assignment.recording_id] = []
                assignments[assignment.recording_id].push( assignment )
            }

            setPreviousAssignments( assignments )
        } catch ( err ) {
            console.log( err )
            setRecordingsFetchError( { message: err?.response?.data?.message || "Something went wrong! Failed to fetch the available recordings.", code: err?.response?.status || 500 } )
        }
    }, [] )

    useEffect( () => {
        fetchQuizDetails()
        fetchPreviousAssignments()
    }, [fetchQuizDetails, fetchPreviousAssignments] )

    useEffect( () => {
        fetchAvailableHybridClassRecordings()
    }, [fetchAvailableHybridClassRecordings] )


    return (
        <Box>

            {foundPreviousAssignmentDuplication && <ConfirmDialog
                status={foundPreviousAssignmentDuplication}
                themeColor="primary"
                cancelAction={cancelFromDuplicateAssignment}
                actionName='Assign Recording'
                confirmAction={() => handleRecordingSelect( previousAssignmentDuplicateDetails?.recording, true )}
                message={
                    <Typography sx={{ fontSize: "0.875rem" }}>
                        The recording{" "}
                        <Typography component="em" sx={{ fontSize: "inherit" }}>
                            {previousAssignmentDuplicateDetails?.title}
                        </Typography>{" "}
                        has already been assigned to a class in this {previousAssignmentDuplicateDetails?.assigned_group?.includes( "-" ) ? "section" : "batch"}. Do you want to assign it again?
                    </Typography>
                }
            />}

            <Dialog disableRestoreFocus slotProps={{ container: { sx: { alignItems: { sm: "center", xs: "flex-end" } } }, paper: { sx: { maxWidth: "700px", width: { sm: "90%", xs: "100%" }, maxHeight: "90%" } } }} fullScreen={xs} open={assignMode}>
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "safe flex-start" }}>
                    <Box padding="10px 20px" borderBottom={border[1]} gap={layout.cardsGap} display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h1" fontWeight="500" component="h1" fontSize="16px">Select Hybrid Class Recording</Typography>
                        <IconButton disabled={uploading} onClick={cancelUpload}>
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Box>
                    {classDetails && assignStep === "recording-selection" && <Box sx={{ p: layout.pagePadding }}>
                        <Typography sx={{ fontSize: "0.875rem", display: "block" }} gutterBottom color="textSecondary" component="p">
                            Select a recording for <Typography component="span" sx={{ fontSize: "inherit", fontStyle: "italic" }} color="textPrimary">{classDetails?.subject_name_short} - {classDetails.course_name}</Typography> from the list below to assign it to this class.
                        </Typography>
                        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                            <Box sx={{ width: "140px", flexShrink: 0 }}>
                                <CustomSelectField autoFocus sx={{ fontWeight: 300 }} id={`module_number`} size="small" value={subjectModule} onChange={event => setSubjectModule( event.target.value )} data={moduleOptions} placeholder="Module" />
                            </Box>
                            <CustomSearchBar placeholder="Search by title..." value={titleSearch} onChange={event => setTitleSearch( event.target.value )} width="auto" style={{ flexGrow: 1 }} />
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            {recordingsFetchError && <TryAgainMessageBlock loading={loadingRecordings} onBack={cancelUpload} err={recordingsFetchError?.message} code={recordingsFetchError?.code} getData={fetchAvailableHybridClassRecordings} />}
                            {/* {!recordingsFetchError && loadingRecordings && <Message  /> } */}
                            {!recordingsFetchError && !loadingRecordings && visibleRecordings.length === 0 && <Message
                                title={titleSearch.trim() ? "Couldn't find any recordings matching your search." : "Couldn't find any recordings for the selected subject and module."}
                                actions={[
                                    { enabled: !!titleSearch?.trim() || !!subjectModule, onClick: () => ( setSubjectModule( "" ), setTitleSearch( '' ) ), label: "Remove Filters", startIcon: Icons.default.FilterAltOff, variant: "outlined", sx: { borderRadius: 5 } },
                                    { onClick: fetchAvailableHybridClassRecordings, label: "Retry", startIcon: Icons.default.Sync, variant: "outlined", sx: { borderRadius: 5 } },
                                ]}
                                img={noRecordings}
                            />}
                            {!recordingsFetchError && !loadingRecordings && visibleRecordings.length > 0 && <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 500 }}>
                                {visibleRecordings.map( ( recording, idx ) => <Box key={recording.id}>
                                    {idx !== 0 && <Divider />}
                                    <RecordingBlock onSelect={handleRecordingSelect} recording={recording} key={recording.id} />
                                </Box> )}
                            </Box>}
                            {loadingRecordings && !recordingsFetchError && loadingSkeleton}
                        </Box>
                    </Box>}
                    {classDetails && assignStep === "quiz-config" && <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Box
                            position="relative"
                            width="100%"
                            maxHeight={{ md: "initial", xs: "200px" }}
                            sx={{ aspectRatio: "16 / 9" }}
                            borderRadius={{ xs: 0, md: layout.borderRadius }}
                            overflow="hidden"
                            bgcolor={palette.contentBg}
                            color="#fff"
                        >
                            {!loadingSelectedRecording && selectedRecording?.recording_url && (
                                <VideoPlayer
                                    containerHeight="100%"
                                    editMode editOptions={VIDEO_PLAYER_EDIT_OPTIONS} onEventsChange={handleEventsUpdate} events={questionEventsRef.current}
                                    key={selectedRecording.recording_url}
                                    src={selectedRecording.recording_url}
                                    poster={selectedRecording.thumbnail_url || undefined}
                                    onCanPlay={() => setIsVideoReady( true )}
                                    controls
                                    preload="metadata"
                                    playsInline
                                />
                            )}
                            {( loadingSelectedRecording || !selectedRecording?.recording_url ) && (
                                <Box sx={{ inset: 0 }} display="flex" flexDirection="column" gap="12px" alignItems="center" justifyContent="center" textAlign="center" padding="16px" >
                                    {loadingSelectedRecording
                                        ? <Skeleton sx={{ width: "100%", height: 300 }} />
                                        : <>
                                            <Typography fontSize="13px">{"No video is available for this recording."}</Typography>
                                        </>
                                    }
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ p: "10px", flexGrow: 1 }}>
                            <Box sx={{ my: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontSize: "0.65rem", mb: 1 }} >TITLE</Typography>
                                <Typography className='truncate-at-2' variant="h6" sx={{ textTransform: "capitalize" }} component="p">{selectedRecording?.title}</Typography>
                            </Box>
                            <Box sx={{ my: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontSize: "0.65rem" }} >TOPICS COVERED</Typography>
                                <Box gap="5px" alignItems="center" display="flex" flexWrap="wrap">
                                    {selectedRecording?.topics_covered?.map( topic => (
                                        <CustomChip key={topic} value={topic} />
                                    ) )}
                                </Box>
                            </Box>
                            <Box sx={{ my: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontSize: "0.65rem", mb: 1 }} >UPLOAD DETAILS</Typography>
                                <Box sx={{ mt: 0.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    <CustomChip value={`Size: ${getFileSizeFromBytes( selectedRecording?.recording_metadata?.size )}`} />
                                    <CustomChip value={`Duration: ${formatVideoDuration( selectedRecording?.recording_metadata?.duration )}`} />
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ bgcolor: palette.contentBg, zIndex: 10, position: "sticky", bottom: 0, p: 2, borderTop: border[1], display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Button startIcon={Icons.default.ArrowBack} sx={{ textTransform: "initial" }} color="greyed" size="small" onClick={() => {
                                setSelectedRecording( null )
                                setAssignStep( "recording-selection" )
                            }} >Select a Different Video</Button>
                            <Button startIcon={Icons.default.CheckIcon} disabled={!isVideoReady} variant="contained" disableElevation size="small" onClick={assignRecording} >Assign Recording To Class</Button>
                        </Box>
                    </Box>}
                </Box>
            </Dialog>

            <Box display="flex" flexDirection="column" gap="10px" alignItems="center">
                <Button disabled={quizFetchError} onClick={() => setAssignMode( true )} tabIndex={-1} startIcon={Icons.default.Movie} component="label" size={buttonSize} variant={buttonVariant} color={buttonColor} disableElevation sx={{ borderRadius: "20px" }} >
                    Select Recording
                </Button>
                {quizFetchError && <Typography variant='caption' color='error'  >Failed to fetch quiz details! <Typography component="button" fontSize="inherit" onClick={fetchQuizDetails} sx={{ cursor: 'pointer', textDecoration: "underline", background: "none", border: "none", fontWeight: 600, color: "inherit", }}>Try again.</Typography> </Typography>}
            </Box>
        </Box>
    )
} )

export default AddNewRecordingButton
